// src/App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ContextMenu from './ContextMenu';
import CustomNode from './CustomNode';
import './App.css';

function App() {
  // **State-Definitionen**
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // **Webhook URLs**
  const CUSTOMERS_URL = 'https://n8n.elexion.de/webhook/mapping/customers';
  const PROJECTS_URL = (customerId) =>
    `https://n8n.elexion.de/webhook/143376dc-da0c-4133-8113-66c573808a73/mapping/projects/customerId/${customerId}`;
  const TICKETS_URL = (projectId) =>
    `https://n8n.elexion.de/webhook/143376dc-da0c-4133-8113-66c573808a73/mapping/tickets/${projectId}`;
  const LV_URL = (projectId) =>
    `https://n8n.elexion.de/webhook/bf5363a3-117d-4fca-8489-800e8578e559/mapping/lv/projectID/${projectId}`;
  const LV_POSITIONS_URL = (lvId) =>
    `https://n8n.elexion.de/webhook/bf5363a3-117d-4fca-8489-800e8578e559/mapping/lv-positions/lvId/${lvId}`;
  const MAPPINGS_URL = (projectId) =>
    `https://n8n.elexion.de/webhook/bf5363a3-117d-4fca-8489-800e8578e559/mapping/mapping/projectId/${projectId}`;
  const MAPPING_UPDATE_URL =
    'https://n8n.elexion.de/webhook/mapping/mappingupdate';
  const MAPPING_DELETE_URL =
    'https://n8n.elexion.de/webhook/mapping/mappingdelete';

  // **Memoisierung der nodeTypes**
  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

  // **Laden der Kunden**
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(CUSTOMERS_URL);
        if (!res.ok) {
          throw new Error(`Fehler beim Abrufen der Kunden, Status: ${res.status}`);
        }
        const customerData = await res.json();
        console.log('Kunden:', customerData);
        if (Array.isArray(customerData)) {
          // Filtere Kunden mit gültiger customer_id (inklusive 0)
          const validCustomers = customerData.filter(
            (c) => c.customer_id !== undefined && c.customer_id !== null
          );
          setCustomers(validCustomers);
        } else {
          console.error('Kunden-Daten sind kein Array:', customerData);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Kunden:', err);
      }
    };

    fetchCustomers();
  }, []);

  // **Laden der Projekte basierend auf ausgewähltem Kunden**
  useEffect(() => {
    if (selectedCustomer) {
      const fetchProjects = async () => {
        try {
          const res = await fetch(PROJECTS_URL(selectedCustomer.customer_id));
          if (!res.ok) {
            throw new Error(`Fehler beim Abrufen der Projekte, Status: ${res.status}`);
          }
          const projectData = await res.json();
          console.log('Projekte:', projectData);
          if (Array.isArray(projectData)) {
            setProjects(projectData);
          } else {
            console.error('Projekt-Daten sind kein Array:', projectData);
          }
        } catch (err) {
          console.error('Fehler beim Laden der Projekte:', err);
        }
      };

      fetchProjects();
    }
  }, [selectedCustomer]);

  // **Laden von LV, LV-Positionen und Tickets basierend auf ausgewähltem Projekt**
  useEffect(() => {
    if (selectedProject) {
      // **Reset Nodes und Edges**
      setNodes([]);
      setEdges([]);

      const loadProjectData = async () => {
        try {
          // **Laden der LVs für das Projekt**
          const lvResponse = await fetch(LV_URL(selectedProject.project_id));
          if (!lvResponse.ok) {
            throw new Error(`Fehler beim Abrufen der LVs, Status: ${lvResponse.status}`);
          }
          const lvData = await lvResponse.json();
          console.log('LV-Kopf:', lvData);
          if (!Array.isArray(lvData)) {
            throw new Error('LV-Daten sind kein Array');
          }

          // **Erstellen der LV-Nodes (Links)**
          const lvNodes = lvData.map((lv, idx) => ({
            id: `lv-${lv.lv_id}`,
            type: 'custom',
            data: {
              label: <strong>{lv.lv_name}</strong>,
              style: { background: '#ffc107', color: '#000' },
            },
            position: { x: 50, y: idx * 300 + 100 }, // Links positioniert mit mehr Abstand
            draggable: true, // Draggable
          }));

          // **Laden der LV-Positionen für alle LVs**
          const lvPositionPromises = lvData.map((lv, lvIndex) =>
            fetch(LV_POSITIONS_URL(lv.lv_id))
              .then((res) => {
                if (!res.ok) {
                  throw new Error(`Fehler beim Abrufen der LV-Positionen, Status: ${res.status}`);
                }
                return res.json();
              })
              .then((lvPosData) => {
                console.log(`LV-Positionen für LV_ID ${lv.lv_id}:`, lvPosData);
                if (!Array.isArray(lvPosData)) {
                  throw new Error(`LV-Positionen-Daten sind kein Array für LV_ID ${lv.lv_id}`);
                }
                return lvPosData.map((lvp, idxPos) => ({
                  id: `lvpos-${lvp.lv_position_id}`,
                  type: 'custom',
                  data: {
                    label: (
                      <>
                        <strong>Position {lvp.position_number}</strong>
                        <br />
                        Beschreibung: {lvp.description} {/* Beschreibung hinzufügen */}
                        <br />
                        Menge: {lvp.quantity}
                        <br />
                        Einheit: {lvp.unit}
                        <br />
                        Preis: {lvp.unit_price}
                      </>
                    ),
                    style: { background: '#28a745', color: '#fff', width: 250 }, // Breiter machen
                    parentLvId: lv.lv_id, // Speichert die zugehörige LV-ID
                  },
                  position: { x: 250, y: lvIndex * 300 + idxPos * 100 + 100 }, // Dynamische y-Position
                  draggable: true, // Draggable
                }));
              })
          );

          // **Warten auf alle LV-Positionen**
          const lvPosResults = await Promise.all(lvPositionPromises);
          // **Flach machen des Arrays von Arrays**
          const lvPosNodes = lvPosResults.flat();

          // **Erstellen von Edges zwischen LV und LV-Positionen**
          const lvToLvPosEdges = [];
          lvPosNodes.forEach((lvPos) => {
            const parentLvId = `lv-${lvPos.data.parentLvId}`;
            lvToLvPosEdges.push({
              id: `edge-lvpos-${lvPos.id}`,
              source: parentLvId,
              target: lvPos.id,
              animated: false,
              style: { stroke: '#0000FF' }, // Blau für LV zu LV-Position
              label: '', // Optional: Sie können ein Label hinzufügen, wenn gewünscht
            });
          });

          // **Laden der Tickets für das Projekt**
          const ticketsResponse = await fetch(TICKETS_URL(selectedProject.project_id));
          if (!ticketsResponse.ok) {
            throw new Error(`Fehler beim Abrufen der Tickets, Status: ${ticketsResponse.status}`);
          }
          const ticketData = await ticketsResponse.json();
          console.log('Tickets:', ticketData);
          if (!Array.isArray(ticketData)) {
            throw new Error('Tickets-Daten sind kein Array');
          }

          // **Erstellen der Ticket-Nodes (Rechts) mit mehr Details**
          const ticketNodes = ticketData.map((t, idx) => ({
            id: `ticket-${t.ticket_id}`,
            type: 'custom',
            data: {
              label: (
                <>
                  <strong>{t.title}</strong>
                  <br />
                  Beschreibung: {t.description}
                  <br />
                  Status: {t.status}
                  <br />
                  Priorität: {t.priority} {/* Zusätzliche Details */}
                  <br />
                  Verantwortlich: {t.assignee} {/* Zusätzliche Details */}
                </>
              ),
              style: { background: '#007bff', color: '#fff', width: 300 }, // Breiter machen
              projectId: selectedProject.project_id, // project_id hinzufügen
            },
            position: { x: 450, y: idx * 300 + 100 }, // Rechts neben LV-Positionen mit mehr Abstand
            draggable: true, // Draggable für Tickets
          }));

          // **Laden der Mapping-Tickets (Edges) (Tickets zu LV-Positionen) Korrigierte Richtung**
          const mappingsResponse = await fetch(MAPPINGS_URL(selectedProject.project_id));
          if (!mappingsResponse.ok) {
            throw new Error(`Fehler beim Abrufen der Mappings, Status: ${mappingsResponse.status}`);
          }
          const mappingData = await mappingsResponse.json();
          console.log('Mapping Data:', mappingData);
          if (!Array.isArray(mappingData)) {
            throw new Error('Mapping-Daten sind kein Array');
          }

          // **Erstellen der Mapping-Edges (Tickets zu LV-Positionen) Korrigierte Richtung**
          const mappingEdges = mappingData.map((m) => ({
            id: `edge-${m.ticket_lv_id}`,
            source: `lvpos-${m.lv_position_id}`, // **Source ist LV-Position**
            target: `ticket-${m.ticket_id}`, // **Target ist Ticket**
            animated: false,
            style: { stroke: '#555' }, // Standardfarbe für Tickets zu LV-Positionen
            label: `Menge verwendet: ${m.quantity_used}`,
            labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: '#fff', stroke: '#fff' },
          }));

          // **Setzen der Nodes und Edges in einem einzigen Update**
          setNodes([...lvNodes, ...lvPosNodes, ...ticketNodes]);
          setEdges([...lvToLvPosEdges, ...mappingEdges]);
        } catch (error) {
          console.error('Fehler beim Laden der Projekt-Daten:', error);
        }
      };

      // **Aufruf der Datenladefunktion**
      loadProjectData();
    }
  }, [selectedProject]); // **Abhängigkeit: selectedProject**

  // **Handler für das Verbinden von Nodes (Erstellen von Edges)**
  const onConnect = useCallback(
    (params) => {
      console.log('Neue Verbindung:', params);

      // **Überprüfen der Verbindung:**
      // Source: LV-Position (lvpos-*)
      // Target: Ticket (ticket-*)
      const sourceType = params.source.split('-')[0];
      const targetType = params.target.split('-')[0];

      // **Nur erlauben, dass LV-Positionen mit Tickets verbunden werden**
      if (sourceType === 'lvpos' && targetType === 'ticket') {
        setEdges((eds) => addEdge(params, eds));

        // **Extrahiere IDs aus den Node-IDs**
        const ticketId = parseInt(params.target.split('-')[1], 10);
        const lvPositionId = parseInt(params.source.split('-')[1], 10);

        // **API-Call ans Backend / n8n, um das Mapping zu speichern**
        fetch(MAPPING_UPDATE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: selectedProject.project_id, // project_id hinzufügen
            ticketId,
            lvPositionId,
          }),
        })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`Fehler beim Speichern des Mappings, Status: ${res.status}`);
            }
            return res.json();
          })
          .then((data) => {
            console.log('Mapping gespeichert, ticket_lv_id = ', data.ticket_lv_id);
            // **Aktualisiere die Edge-ID mit der neuen Mapping-ID**
            setEdges((eds) =>
              eds.map((edge) =>
                edge.id === params.id
                  ? { ...edge, id: `edge-${data.ticket_lv_id}`, label: `Menge verwendet: ${data.quantity_used}` }
                  : edge
              )
            );
          })
          .catch((err) => {
            console.error('Fehler beim Speichern des Mappings:', err);
          });
      } else {
        // **Verbindungen in andere Richtungen verhindern**
        alert('Verbindungen sind nur von LV-Positionen zu Tickets erlaubt.');
      }
    },
    [MAPPING_UPDATE_URL, selectedProject]
  );

  // **Handler für das Löschen von Edges**
  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      console.log('Zu löschende Edges:', deletedEdges);

      deletedEdges.forEach((edge) => {
        // **Extrahiere die Mapping-ID aus der Edge-ID**
        const ticketLvId = parseInt(edge.id.replace('edge-', ''), 10);
        if (isNaN(ticketLvId)) {
          console.error('Ungültige ticket_lv_id aus Edge-ID:', edge.id);
          return;
        }

        // **API-Call ans Backend / n8n, um das Mapping zu löschen**
        fetch(MAPPING_DELETE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticketLvId }),
        })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`Fehler beim Löschen des Mappings, Status: ${res.status}`);
            }
            return res.json();
          })
          .then((data) => {
            console.log('Mapping erfolgreich gelöscht:', data);
          })
          .catch((err) => {
            console.error('Fehler beim Löschen des Mappings:', err);
          });
      });
    },
    [MAPPING_DELETE_URL]
  );

  // **Handler für Edge-Klicks (Selektieren der Edge und Öffnen des Kontextmenüs)**
  const onEdgeClick = useCallback((event, edge) => {
    event.preventDefault();
    setSelectedEdge(edge);
    setContextMenuPos({ x: event.clientX, y: event.clientY });
  }, []);

  // **Handler zum Löschen der ausgewählten Edge via Kontextmenü**
  const handleDelete = useCallback(() => {
    if (selectedEdge) {
      // **Lösche die Edge aus dem lokalen State**
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));

      // **Extrahiere die Mapping-ID aus der Edge-ID**
      const ticketLvId = parseInt(selectedEdge.id.replace('edge-', ''), 10);
      if (isNaN(ticketLvId)) {
        console.error('Ungültige ticket_lv_id aus Edge-ID:', selectedEdge.id);
        return;
      }

      // **API-Call ans Backend / n8n, um das Mapping zu löschen**
      fetch(MAPPING_DELETE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticketLvId }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Fehler beim Löschen des Mappings, Status: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          console.log('Mapping erfolgreich gelöscht:', data);
          setSelectedEdge(null);
          setContextMenuPos(null);
        })
        .catch((err) => {
          console.error('Fehler beim Löschen des Mappings:', err);
        });
    }
  }, [selectedEdge, MAPPING_DELETE_URL]);

  // **Handler zum Schließen des Kontextmenüs**
  const handleCloseContextMenu = useCallback(() => {
    setContextMenuPos(null);
    setSelectedEdge(null);
  }, []);

  // **Globaler Keydown-Listener zum Löschen ausgewählter Edges**
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedEdge) {
        handleDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // **Cleanup**
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedEdge, handleDelete]);

  // **Handler für Kunden-Auswahl**
  const handleCustomerChange = (e) => {
    const customerId = parseInt(e.target.value, 10);
    const customer = customers.find((c) => c.customer_id === customerId);
    setSelectedCustomer(customer || null);
    // **Reset Projekte und andere States**
    setProjects([]);
    setSelectedProject(null);
    setNodes([]);
    setEdges([]);
    setSelectedEdge(null);
    setContextMenuPos(null);
  };

  // **Handler für Projekt-Auswahl**
  const handleProjectChange = (e) => {
    const projectId = parseInt(e.target.value, 10);
    const project = projects.find((p) => p.project_id === projectId);
    setSelectedProject(project || null);
    // **Reset Nodes und Edges bei Auswahl eines neuen Projekts**
    setNodes([]);
    setEdges([]);
    setSelectedEdge(null);
    setContextMenuPos(null);
  };

  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      {!selectedCustomer ? (
        <div className="selection-container">
          <h2>Kunde auswählen</h2>
          <select title="kunden" onChange={handleCustomerChange} defaultValue="">
            <option value="">
              Bitte wähle einen Kunden
            </option>
            {customers.map((customer) => (
              <option key={customer.customer_id} value={customer.customer_id}>
                {customer.customer_name || 'Unnamed Customer'}
              </option>
            ))}
          </select>
        </div>
      ) : !selectedProject ? (
        <div className="selection-container">
          <h2>Projekt auswählen</h2>
          <select onChange={handleProjectChange} defaultValue="">
            <option value="">
              Bitte wähle ein Projekt
            </option>
            {projects.map((project) => (
              <option key={project.project_id} value={project.project_id}>
                {project.project_name || 'Unnamed Project'}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div className="project-header">
            <h3>Projekt: {selectedProject.project_name}</h3>
          </div>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgesDelete={onEdgesDelete}
            onEdgeClick={onEdgeClick}
            deleteKeyCode={46} // Entf-Taste
            selectNodesOnDrag={true}
            multiSelectionKeyCode={17} // Strg-Taste
            fitView
            nodesConnectable={true} // Alle Nodes connectable
            elementsSelectable={true}
            nodeTypes={nodeTypes} // Verwenden der memoisierten nodeTypes
            draggable={true}
          >
            <Background color="#aaa" gap={16} />
            <Controls />
            <MiniMap
              nodeStrokeColor={(n) => {
                if (n.type === 'custom') {
                  if (n.data.style.background === '#007bff') return '#007bff';
                  if (n.data.style.background === '#28a745') return '#28a745';
                  if (n.data.style.background === '#ffc107') return '#ffc107';
                }
                return '#eee';
              }}
              nodeColor={(n) => {
                if (n.type === 'custom') {
                  return n.data.style.background;
                }
                return '#fff';
              }}
            />
          </ReactFlow>

          {/* Kontextmenü */}
          {selectedEdge && contextMenuPos && (
            <ContextMenu
              position={contextMenuPos}
              onDelete={handleDelete}
              onClose={handleCloseContextMenu}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;